import type { api } from '@vrplatform/data';
import { createStore, useStore } from 'zustand';
import type { useJournalEntriesTableFilter } from './useJournalEntriesTableFilter';

type QueryFilter = ReturnType<typeof useJournalEntriesTableFilter>['filter'];

type StateFilter = {
  [K in keyof QueryFilter]: QueryFilter[K] | undefined;
};

export type JournalEntriesApiFilter = NonNullable<
  api['/reports/journal-entries']['get']['parameters']['query']
> & {
  excludeApAppendix?: boolean;
};

export type JournalEntryReportDrilldown =
  | {
      type: 'journalEntryReport';
      filter: JournalEntriesApiFilter;
      label?: string;
      measure?: 'periodActivity' | 'balance';
    }
  | null
  | undefined;

export type JournalEntriesModalArgs = {
  filter: JournalEntriesApiFilter;
  label?: string;
};

export const getJournalEntryReportFilter = (
  drilldown: JournalEntryReportDrilldown
) => (drilldown?.type === 'journalEntryReport' ? drilldown.filter : undefined);

export const getJournalEntriesModalArgs = (
  drilldown: JournalEntryReportDrilldown
): JournalEntriesModalArgs | undefined => {
  if (drilldown?.type !== 'journalEntryReport') {
    return undefined;
  }

  return drilldown.label
    ? { filter: drilldown.filter, label: drilldown.label }
    : { filter: drilldown.filter };
};

type State = {
  args: JournalEntriesModalArgs | null;
  filter: StateFilter;
};

type Actions = {
  open: (args: JournalEntriesModalArgs) => void;
  close: () => void;
  setFilter: (filter: Partial<StateFilter>) => void;
};

const initialState: State = {
  args: null,
  filter: {
    search: undefined,
    listingIds: undefined,
    listingScope: undefined,
    contactIds: undefined,
    reservationIds: undefined,
    party: undefined,
    amount: undefined,
    type: undefined,
    classifications: undefined,
    accountIds: undefined,
    date: undefined,
    status: undefined,
    subType: undefined,
    currency: undefined,
    view: undefined,
    ledger: undefined,
    published: undefined,
    bookingChannels: undefined,
    openingBalanceType: undefined,
    listingOwnershipPeriodIds: undefined,
  },
};

export const journalEntriesModalStore = createStore<State & Actions>(
  (set, get) => ({
    close: () => set({ args: initialState.args, filter: initialState.filter }),
    open: (args) => set({ args }),
    setFilter: (filter) => set({ filter: { ...get().filter, ...filter } }),
    ...initialState,
  })
);

export const useJournalEntriesModal = () => {
  const open = useStore(journalEntriesModalStore, (state) => state.open);
  const close = useStore(journalEntriesModalStore, (state) => state.close);

  return {
    open,
    close,
  };
};

export const useJournalEntriesModalFilter = (): {
  filter: StateFilter;
  setFilter: (filter: Partial<StateFilter>) => void;
} => {
  const filter = useStore(journalEntriesModalStore, (state) => state.filter);
  const setFilter = useStore(
    journalEntriesModalStore,
    (state) => state.setFilter
  );

  return {
    filter,
    setFilter,
  };
};

export const useJournalEntriesModalArgs = () => {
  const args = useStore(journalEntriesModalStore, (state) => state.args);

  return args;
};
